import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { useTextoCadaMeses } from '#components/mobile/anual/PagosFijosMensuales';
import { Gavi } from '#components/mobile/travesia/Gavi';
import { Boton } from '#components/mobile/ui/Boton';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import { color, espacio, texto } from '#components/mobile/ui/tokens';
import { registrarUso } from '#uso';

import type { SerieCobro } from './cobros';
import { useFormatosGavi } from './formatos';

/** Como mucho, tantas filas a la vez (las demás salen al responder). */
const MAX_FILAS = 4;

type EncontradosGaviProps = {
  encontrados: readonly SerieCobro[];
  nombreCategoria: (id: string | null) => string | null;
  /** «Es fijo»: enciende «Pago fijo» en su categoría. */
  onFijo: (serie: SerieCobro) => void;
  /** «No»: Gavi no vuelve a sacarlo. */
  onNo: (serie: SerieCobro) => void;
};

/**
 * Arriba de «Pagos fijos» (Altamar): los cobros que se repiten y aún no son
 * pago fijo, con «Es fijo» y «No». Gavi se ladea al encontrar algo.
 */
export function EncontradosGavi({
  encontrados,
  nombreCategoria,
  onFijo,
  onNo,
}: EncontradosGaviProps) {
  const { t } = useTranslation();
  const { euros } = useFormatosGavi();
  const textoCada = useTextoCadaMeses();
  const [aplauso, setAplauso] = useState(0);

  if (encontrados.length === 0) {
    return null;
  }
  const mensuales = encontrados.every(s => s.cada === 1);
  const filas = encontrados.slice(0, MAX_FILAS);

  return (
    <Tarjeta
      style={{ margin: `0 ${espacio.margen}px`, gap: 12 }}
      data-testid="gavi-encontrados"
    >
      <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
        <Gavi
          estado={aplauso > 0 ? 'aplaude' : 'ladea'}
          clave={aplauso}
          ancho={44}
          style={{ flexShrink: 0 }}
        />
        <Text style={{ ...texto.cuerpo, flex: 1, color: color.fg }}>
          {mensuales
            ? t(
                'I have found {{count}} charges that repeat every month and are not here.',
                { count: encontrados.length },
              )
            : t(
                'I have found {{count}} charges that repeat and are not here.',
                { count: encontrados.length },
              )}
        </Text>
      </View>
      {filas.map(s => {
        const categoria = nombreCategoria(s.categoria);
        return (
          <View
            key={s.clave}
            data-testid="gavi-encontrado"
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              borderTop: `1px solid ${color.line}`,
              paddingTop: 10,
            }}
          >
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: color.fg,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.comercio}
              </Text>
              <Text style={{ fontSize: 12, color: color.fg3 }}>
                {[
                  euros(s.importe),
                  s.cada === 1
                    ? t('day {{day}}', { day: s.dia })
                    : textoCada(s.cada),
                  categoria
                    ? t('in {{category}}', { category: categoria })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
            </View>
            <Boton
              variante="primario"
              onPress={() => {
                setAplauso(a => a + 1);
                registrarUso('gavi_pagos_fijos', { accion: 'es-fijo' });
                onFijo(s);
              }}
              style={{ minHeight: 36, padding: '0 12px', fontSize: 13 }}
            >
              <Trans>It is fixed</Trans>
            </Boton>
            <Boton
              variante="fantasma"
              onPress={() => {
                registrarUso('gavi_pagos_fijos', { accion: 'no-es-fijo' });
                onNo(s);
              }}
              style={{ minHeight: 36, padding: '0 12px', fontSize: 13 }}
            >
              <Trans>No</Trans>
            </Boton>
          </View>
        );
      })}
    </Tarjeta>
  );
}
